import { useMutation } from "@blitzjs/rpc";
import { useReCaptcha } from "next-recaptcha-v3";

import Form, { FORM_ERROR } from "app/core/components/Form";
import LabeledTextField from "app/core/components/LabeledTextField";
import { useAnalytics } from "app/core/hooks/useAnalytics";
import { useQueryParam } from "app/core/hooks/useQueryParam";

import createRecaptchaAssessment from "app/auth/mutations/createRecaptchaAssessment";
import forgotPassword from "app/auth/mutations/forgotPassword";
import { ForgotPassword } from "app/auth/validations";

const action = "set_password";

export function ForgotPasswordForm() {
  const queryEmail = useQueryParam("email", "string");
  const [forgotPasswordMutation, { isSuccess }] = useMutation(forgotPassword);
  const [invokeRecaptchaVerify] = useMutation(createRecaptchaAssessment);
  const recaptcha = useReCaptcha();
  const { trackEvent } = useAnalytics();

  const handleSubmit = async (values: any) => {
    if (!recaptcha.loaded) return;

    try {
      // Must pass recaptcha
      const token = await recaptcha.executeRecaptcha(action);
      const recaptchaRes = await invokeRecaptchaVerify({ token });
      if (!recaptchaRes.success) {
        throw new Error("ReCAPTCHA failed", { cause: recaptchaRes });
      }
      trackEvent("Forgot Password Start");
      await forgotPasswordMutation(values);
      trackEvent("Forgot Password Success");
    } catch (error: any) {
      trackEvent("Forgot Password Error");
      return {
        [FORM_ERROR]: "Sorry, we had an unexpected error. Please try again.",
      };
    }
  };

  return (
    <>
      {isSuccess ? (
        <div>
          <h2 className="font-medium pb-2">Request submitted</h2>
          <p>
            If your email is in our system, you will receive an email to set your password shortly.
          </p>
        </div>
      ) : (
        <Form
          submitText="Send me a link"
          schema={ForgotPassword}
          initialValues={{ email: queryEmail || "" }}
          onSubmit={handleSubmit}
        >
          <LabeledTextField
            className="w-full"
            name="email"
            label="Enter your email to get a magic link to set a new password."
            placeholder="Email"
          />
        </Form>
      )}
    </>
  );
}
